/**
 * ThreadView.tsx — /community/thread/:threadId. The thread's own title + body (its description,
 * NOT a comment — not reply/like/report-able), then its nested COMMENT tree (Reddit-style: every
 * top-level comment is independent, to three levels below it, CH-F09), in conversational order
 * (CH-F08). Each comment carries the author's name or a departure marker (CH-F04), any
 * genuinely-held role markers (CH-F05), an "edited" marker (CH-F10), a like control (CH-F06), and —
 * for the caller's own comments — edit and delete (CH-F10). A "Leave a comment" box creates a new
 * standalone top-level comment; a reply-in-place box sits under every comment (CH-F03/F19). The
 * whole thread can be saved privately (CH-F12).
 *
 * ON THE HOUSE DESIGN SYSTEM. Same `.cm-*` frame as /community and the channel page: a full-bleed
 * navy band (breadcrumb + title + the starter's byline), the description as prose beneath it, a
 * thread action bar, then the comment tree as spaced cards with indented reply rails. The old
 * `--tb-*` slice (COMMUNITY_STYLES, ProvisionalBanner, `.tb-post*`) is gone from this file. The
 * byline atoms still come from the security-reviewed helpers (AuthorAvatar / AuthorName / Markers),
 * which emit `.tb-avatar` / `.tb-post-author` / `.tb-marker`; communityPage.css restyles those three
 * on house tokens, so the helpers are untouched. `ThreadView` and `threadLoader` keep their names —
 * router.tsx is untouched — and every backend call is byte-for-byte what it was before the reskin.
 *
 * SECURITY: an unreadable or missing thread returns 404 from the backend, surfaced here as a plain
 * not-found (no existence leak, CH-F15). Every mutation derives the actor from the session
 * server-side; the browser sends only ids and text. Like/reply/edit/delete update in place by
 * re-running the loader (useRevalidator) — no optimistic client cache to drift from the server.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useLoaderData, useRevalidator, useSearchParams } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';
import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  type ForumRule,
  type PostNode,
  type ThreadDetail,
} from './api';
import { AuthorAvatar, AuthorName, Markers, SignedOut, relativeWhen } from './shared';
import { CHANNELS_BY_SLUG } from './shared';
// NOTE: communityPage.css (the `.cm-*` frame) is imported at the app root (src/main.tsx), NOT here —
// see the note in Community.tsx: a component-level import code-splits into the route chunk and
// flashes the full-bleed navy band in default flow on a cold load.

export type ThreadLoaderData =
  { state: 'ok'; thread: ThreadDetail } | { state: 'signedOut' } | { state: 'notFound' };

/** GET /api/community/threads/:id → the thread tree. 404 = missing-or-forbidden (reported the same). */
export async function threadLoader({ params }: LoaderFunctionArgs): Promise<ThreadLoaderData> {
  const threadId = params.threadId ?? '';
  const res = await apiGet<ThreadDetail>(`/api/community/threads/${threadId}`);
  if (res.kind === 'unauthorized') return { state: 'signedOut' };
  if (res.kind === 'error') {
    if (res.status === 404) return { state: 'notFound' };
    throw new Error(`GET /api/community/threads/${threadId} failed (${res.status})`);
  }
  return { state: 'ok', thread: res.body };
}

/** Count comments (top-level + nested) for the header line. */
function countReplies(posts: PostNode[]): number {
  let n = 0;
  const walk = (node: PostNode) => {
    n += 1;
    node.children.forEach(walk);
  };
  posts.forEach(walk);
  return n;
}

/** The display name of a channel from its slug, for the breadcrumb (falls back to "Community"). */
function channelName(slug: string): string {
  return CHANNELS_BY_SLUG.get(slug)?.name ?? 'Community';
}

/**
 * The reply-in-place box, nested under a specific comment. POSTs a reply under that parent post;
 * the backend caps depth at three below a top-level comment (a reply to a depth-3 post attaches at
 * depth 3, CH-F09).
 */
function ReplyBox({
  threadId,
  parentPostId,
  onCancel,
  onReplied,
}: {
  threadId: string;
  parentPostId: string | null;
  onCancel: () => void;
  onReplied: () => void;
}) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const res = await apiPost<{ post_id: string }>(`/api/community/threads/${threadId}/replies`, {
      parentPostId,
      body: body.trim(),
    });
    setBusy(false);
    if (res.kind === 'ok') {
      onReplied();
      return;
    }
    if (res.kind === 'error' && res.code === 'invalid_request') {
      setError('Please write a reply.');
      return;
    }
    if (res.kind === 'error' && res.status === 403) {
      setError('You cannot post in this channel.');
      return;
    }
    setError('Could not post your reply. Please try again.');
  }

  return (
    <form className="cm-composer" onSubmit={submit}>
      {error ? (
        <p className="cm-error" role="alert">
          {error}
        </p>
      ) : null}
      <fieldset className="cm-composer__fields">
        <label className="cm-field">
          <span>Reply</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={10_000}
            placeholder="Write your reply"
            autoFocus
          />
        </label>
      </fieldset>
      <div className="cm-composer__foot">
        <button className="cm-btn-ghost" type="button" onClick={onCancel}>
          Cancel
        </button>
        <button className="cm-btn-solid" type="submit" disabled={body.trim().length < 1 || busy}>
          {busy ? 'Posting…' : 'Post reply'}
        </button>
      </div>
    </form>
  );
}

/** The edit box for the caller's own post. PATCHes the new body; the backend stamps the edited marker. */
function EditBox({
  post,
  onCancel,
  onSaved,
}: {
  post: PostNode;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [body, setBody] = useState(post.body);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const res = await apiPatch<{ ok: true }>(`/api/community/posts/${post.postId}`, {
      body: body.trim(),
    });
    setBusy(false);
    if (res.kind === 'ok') {
      onSaved();
      return;
    }
    if (res.kind === 'error' && res.code === 'invalid_request') {
      setError('Please write a message.');
      return;
    }
    if (res.kind === 'error' && res.status === 404) {
      setError('This post can no longer be edited.');
      return;
    }
    setError('Could not save your edit. Please try again.');
  }

  return (
    <form className="cm-composer" onSubmit={submit}>
      {error ? (
        <p className="cm-error" role="alert">
          {error}
        </p>
      ) : null}
      <fieldset className="cm-composer__fields">
        <label className="cm-field">
          <span>Edit your message</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={10_000}
            autoFocus
          />
        </label>
      </fieldset>
      <div className="cm-composer__foot">
        <button className="cm-btn-ghost" type="button" onClick={onCancel}>
          Cancel
        </button>
        <button className="cm-btn-solid" type="submit" disabled={body.trim().length < 1 || busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

/**
 * "Leave a comment" — creates a new, standalone, top-level comment (parentPostId always null),
 * independent of every other comment and of the thread's own description. Modeled on ReplyBox's
 * form shape, but collapsed behind a button (like a per-post reply box) rather than always open,
 * so it does not crowd the thread above the comment list.
 */
function CommentComposer({ threadId, onCommented }: { threadId: string; onCommented: () => void }) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const res = await apiPost<{ post_id: string }>(`/api/community/threads/${threadId}/replies`, {
      parentPostId: null,
      body: body.trim(),
    });
    setBusy(false);
    if (res.kind === 'ok') {
      setBody('');
      setOpen(false);
      onCommented();
      return;
    }
    if (res.kind === 'error' && res.code === 'invalid_request') {
      setError('Please write a comment.');
      return;
    }
    if (res.kind === 'error' && res.status === 403) {
      setError('You cannot post in this channel.');
      return;
    }
    setError('Could not post your comment. Please try again.');
  }

  if (!open) {
    return (
      <button className="cm-btn-solid" type="button" onClick={() => setOpen(true)}>
        Leave a comment
      </button>
    );
  }

  return (
    <form className="cm-composer" onSubmit={submit}>
      {error ? (
        <p className="cm-error" role="alert">
          {error}
        </p>
      ) : null}
      <fieldset className="cm-composer__fields">
        <label className="cm-field">
          <span>Leave a comment</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={10_000}
            placeholder="Write your comment"
            autoFocus
          />
        </label>
      </fieldset>
      <div className="cm-composer__foot">
        <button className="cm-btn-ghost" type="button" onClick={() => setOpen(false)}>
          Cancel
        </button>
        <button className="cm-btn-solid" type="submit" disabled={body.trim().length < 1 || busy}>
          {busy ? 'Posting…' : 'Post comment'}
        </button>
      </div>
    </form>
  );
}

/**
 * One comment and its subtree. Renders the author line + markers, the body (or a tombstone when
 * removed), the like control, and — when the comment is the caller's own and not removed — edit and
 * delete. A reply box can be toggled open under any comment. Children indent behind a vertical rail
 * (`.cm-replies`). `onChanged` re-runs the loader so the tree reflects the server after any mutation.
 */
function Post({
  post,
  threadId,
  onChanged,
  highlightPostId,
  onHighlighted,
}: {
  post: PostNode;
  threadId: string;
  onChanged: () => void;
  // Set only when a notification's link named this exact post (?highlight=<postId>) — scrolls it
  // into view and flashes it for ~1s, then fades back to normal. Cleared via onHighlighted so the
  // flash never re-fires on a later revalidate/rerender.
  highlightPostId: string | null;
  onHighlighted: () => void;
}) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState(false);
  const nodeRef = useRef<HTMLLIElement>(null);

  const isHighlightTarget = highlightPostId === post.postId;

  useEffect(() => {
    if (!isHighlightTarget) return;
    nodeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setFlash(true);
    onHighlighted();
    const id = window.setTimeout(() => setFlash(false), 1000);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHighlightTarget]);

  async function toggleLike() {
    setError(null);
    setBusy(true);
    const res = post.likedByViewer
      ? await apiDelete<{ ok: true }>(`/api/community/posts/${post.postId}/like`)
      : await apiPost<{ ok: true }>(`/api/community/posts/${post.postId}/like`);
    setBusy(false);
    if (res.kind === 'ok') {
      onChanged();
      return;
    }
    if (res.kind === 'error' && res.code === 'cannot_like_own_post') {
      setError('You cannot like your own post.');
      return;
    }
    setError('Could not update your like. Please try again.');
  }

  async function remove() {
    setError(null);
    setBusy(true);
    const res = await apiDelete<{ ok: true }>(`/api/community/posts/${post.postId}`);
    setBusy(false);
    if (res.kind === 'ok') {
      onChanged();
      return;
    }
    setError('Could not delete this post. Please try again.');
  }

  return (
    <li ref={nodeRef} className={`cm-comment${flash ? ' cm-comment--flash' : ''}`}>
      <div className="cm-comment__head">
        <AuthorAvatar
          name={post.authorName}
          avatarKind={post.avatarKind}
          avatarBlobRef={post.avatarBlobRef}
          photoBlobRef={post.photoBlobRef}
        />
        <AuthorName name={post.authorName} userId={post.authorUserId} isOwn={post.isOwn} />
        <Markers champion={post.markers.champion} verified={post.markers.verified} />
        <span className="cm-comment__meta">
          · {relativeWhen(post.createdAt)}
          {post.edited ? ' · edited' : ''}
        </span>
      </div>

      {editOpen && !post.removed ? (
        <EditBox
          post={post}
          onCancel={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            onChanged();
          }}
        />
      ) : (
        <p className={`cm-comment__body${post.removed ? ' cm-comment__body--removed' : ''}`}>
          {post.body}
        </p>
      )}

      {error ? (
        <p className="cm-error" role="alert" style={{ marginTop: '8px' }}>
          {error}
        </p>
      ) : null}

      {!post.removed ? (
        <div className="cm-comment-actions">
          {/* No self-like: the backend rejects it, and we hide the control on the caller's own posts. */}
          {!post.isOwn ? (
            <button
              className="cm-comment-action"
              type="button"
              aria-pressed={post.likedByViewer}
              onClick={toggleLike}
              disabled={busy}
            >
              {post.likedByViewer ? '♥' : '♡'} {post.likeCount}
            </button>
          ) : (
            <span className="cm-comment-stat">
              ♥ {post.likeCount} {post.likeCount === 1 ? 'like' : 'likes'}
            </span>
          )}

          <button
            className="cm-comment-action"
            type="button"
            aria-expanded={replyOpen}
            onClick={() => setReplyOpen((v) => !v)}
          >
            {replyOpen ? 'Cancel reply' : '↱ Reply'}
          </button>

          {post.isOwn ? (
            <>
              <button
                className="cm-comment-action"
                type="button"
                aria-expanded={editOpen}
                onClick={() => setEditOpen((v) => !v)}
              >
                {editOpen ? 'Cancel edit' : 'Edit'}
              </button>
              <button className="cm-comment-action" type="button" onClick={remove} disabled={busy}>
                Delete
              </button>
            </>
          ) : (
            // You can report anyone else's post (CH-F14). Not shown on your own posts.
            <ReportButton postId={post.postId} />
          )}
        </div>
      ) : null}

      {replyOpen ? (
        <ReplyBox
          threadId={threadId}
          parentPostId={post.postId}
          onCancel={() => setReplyOpen(false)}
          onReplied={() => {
            setReplyOpen(false);
            onChanged();
          }}
        />
      ) : null}

      {post.children.length > 0 ? (
        <ul className="cm-replies">
          {post.children.map((child) => (
            <Post
              key={child.postId}
              post={child}
              threadId={threadId}
              onChanged={onChanged}
              highlightPostId={highlightPostId}
              onHighlighted={onHighlighted}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/** The save/unsave toggle for the whole thread (CH-F12). Private to the caller. */
function SaveToggle({
  threadId,
  saved,
  onChanged,
}: {
  threadId: string;
  saved: boolean;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function toggle() {
    setError(null);
    setBusy(true);
    const res = saved
      ? await apiDelete<{ ok: true }>(`/api/community/threads/${threadId}/save`)
      : await apiPost<{ ok: true }>(`/api/community/threads/${threadId}/save`);
    setBusy(false);
    if (res.kind === 'ok') {
      onChanged();
      return;
    }
    setError('Could not update your saved threads. Please try again.');
  }

  return (
    <>
      <button
        className="cm-btn-ghost"
        type="button"
        aria-pressed={saved}
        onClick={toggle}
        disabled={busy}
      >
        {saved ? '★ Saved' : '☆ Save thread'}
      </button>
      {error ? (
        <span className="cm-error" role="alert" style={{ margin: 0 }}>
          {error}
        </span>
      ) : null}
    </>
  );
}

/**
 * The Report control for a post (CH-F14). Reporting NEVER removes anything — it flags the post for a
 * moderator to decide, and the post stays visible. One flag per person: a repeat report is a no-op
 * the backend reports as `already`, which we surface as a calm "already reported". A first click
 * opens a confirm step (so a mis-click doesn't file a report); confirming POSTs and shows the result
 * inline. Shown only on posts that are not the caller's own and not already removed.
 */
function ReportButton({ postId }: { postId: string }) {
  const [step, setStep] = useState<'idle' | 'confirm' | 'done'>('idle');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function report() {
    setBusy(true);
    const res = await apiPost<{ ok: true; outcome: 'flagged' | 'already' }>(
      `/api/community/posts/${postId}/report`,
    );
    setBusy(false);
    if (res.kind === 'ok') {
      setStep('done');
      setMessage(
        res.body.outcome === 'already'
          ? 'You have already reported this post — a moderator will review it.'
          : 'Reported. A moderator will review this post; it stays visible until they decide.',
      );
      return;
    }
    // A restricted-channel post returns 404 (no existence leak); any error is reported the same calm way.
    setStep('done');
    setMessage('Could not report this post. Please try again.');
  }

  if (step === 'done') {
    return (
      <span className="cm-comment-inline" role="status">
        {message}
      </span>
    );
  }
  if (step === 'confirm') {
    return (
      <span className="cm-comment-inline">
        Report this post to a moderator?
        <button className="cm-comment-action" type="button" onClick={report} disabled={busy}>
          {busy ? 'Reporting…' : 'Yes, report'}
        </button>
        <button
          className="cm-comment-action"
          type="button"
          onClick={() => setStep('idle')}
          disabled={busy}
        >
          Cancel
        </button>
      </span>
    );
  }
  return (
    <button className="cm-comment-action" type="button" onClick={() => setStep('confirm')}>
      ⚑ Report
    </button>
  );
}

/**
 * A disclosure that fetches and shows the published forum rules (CH-F16). Reachable from every
 * thread (the requirement is "reachable from every channel"); collapsed by default so it does not
 * crowd the thread. Loads GET /api/community/rules on first open.
 */
function ForumRulesDisclosure() {
  const [open, setOpen] = useState(false);
  const [rules, setRules] = useState<ForumRule[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (next && rules === null) {
      const res = await apiGet<{ rules: ForumRule[] }>('/api/community/rules');
      if (res.kind === 'ok') setRules(res.body.rules);
      else setError('Could not load the forum rules.');
    }
  }

  return (
    <>
      <button className="cm-btn-ghost" type="button" aria-expanded={open} onClick={toggle}>
        {open ? 'Hide forum rules' : 'Forum rules'}
      </button>
      {open ? (
        <div className="cm-rules">
          <p className="cm-rules__title">Forum rules</p>
          {error ? (
            <p className="cm-error" role="alert" style={{ margin: 0 }}>
              {error}
            </p>
          ) : rules === null ? (
            <p className="cm-empty">Loading…</p>
          ) : (
            <ol>
              {rules.map((r) => (
                <li key={r.ruleId}>{r.text}</li>
              ))}
            </ol>
          )}
        </div>
      ) : null}
    </>
  );
}

export function ThreadView() {
  const data = useLoaderData() as ThreadLoaderData;
  const revalidator = useRevalidator();
  const onChanged = () => revalidator.revalidate();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightPostId = searchParams.get('highlight');
  // Consumed by the target Post on mount (scrolls + flashes it) — stripped from the URL right after
  // so a later refresh/revalidate never re-triggers the flash.
  const clearHighlight = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('highlight');
        return next;
      },
      { replace: true },
    );
  };

  if (data.state === 'signedOut') return <SignedOut title="Community" returnTo="/community" />;

  if (data.state === 'notFound') {
    return (
      <section aria-labelledby="thread-missing-heading" className="tb-community cm-page">
        <div className="cm-band-bleed">
          <div className="cm-band">
            <div className="cm-band__main">
              <p className="cm-crumb">
                <Link to="/community">← Community</Link>
              </p>
              <h1 id="thread-missing-heading">Thread not found</h1>
              <p className="cm-band__lede">
                This thread does not exist, or is not available to you.
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const { thread } = data;
  const replies = countReplies(thread.posts);

  return (
    <section aria-labelledby="thread-heading" className="tb-community cm-page">
      {/* ── Band: breadcrumb back to the channel, the thread title, and the starter's byline. ── */}
      <div className="cm-band-bleed">
        <div className="cm-band">
          <div className="cm-band__main">
            <p className="cm-crumb">
              <Link to={`/community/${thread.channelSlug}`}>
                ← {channelName(thread.channelSlug)}
              </Link>
            </p>
            <h1 id="thread-heading">{thread.title}</h1>
            <div className="cm-thread-byline">
              <AuthorAvatar
                name={thread.authorName}
                avatarKind={thread.avatarKind}
                avatarBlobRef={thread.avatarBlobRef}
                photoBlobRef={thread.photoBlobRef}
              />
              <AuthorName
                name={thread.authorName}
                userId={thread.authorUserId}
                isOwn={thread.isOwn}
              />
              <Markers champion={thread.markers.champion} verified={thread.markers.verified} />
            </div>
          </div>
        </div>
      </div>

      {/* ── The thread's own description (the opening post — not a comment). ── */}
      <p className="cm-thread-lede">{thread.body}</p>

      {/* ── Thread action bar: comment count · Save · Forum rules · Refresh. ── */}
      <div className="cm-thread-bar">
        <p className="cm-thread-bar__count">
          {replies} {replies === 1 ? 'comment' : 'comments'}
        </p>
        <SaveToggle threadId={thread.threadId} saved={thread.savedByViewer} onChanged={onChanged} />
        <ForumRulesDisclosure />
        <button
          className="cm-btn-ghost cm-btn-ghost--refresh"
          type="button"
          onClick={onChanged}
          disabled={revalidator.state === 'loading'}
        >
          {revalidator.state === 'loading' ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* ── Leave a comment (new standalone top-level comment). ── */}
      <div className="cm-comment-composer">
        <CommentComposer threadId={thread.threadId} onCommented={onChanged} />
      </div>

      {/* The comment tree. Native nested <ul>/<li> conveys the reply nesting to assistive tech; the
          vertical rail (.cm-replies) shows it visually (NFR02). */}
      {thread.posts.length === 0 ? (
        <p className="cm-empty">No comments yet — be the first to reply.</p>
      ) : (
        <ul className="cm-comments" aria-label="Thread comments">
          {thread.posts.map((p) => (
            <Post
              key={p.postId}
              post={p}
              threadId={thread.threadId}
              onChanged={onChanged}
              highlightPostId={highlightPostId}
              onHighlighted={clearHighlight}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
